/**
 * Satellite passes at L2 (IDEAS X4, the sector side). Each faction has one reconnaissance satellite on a fixed orbit
 * that sweeps one system a day in a seeded order over the whole map, repeating — a sensor with a schedule. A pass
 * is a look: the system's owner, garrison and the squads standing there enter the faction's knowledge that day
 * (knowledge.ts `observe`). The orbit is fixed and tracked, so its times are known to everyone: the panel shows
 * when each side next passes a system, and the stand-in times a sneak so it does not land under an enemy pass.
 * Behind `rules.satellites`. Pure.
 */
import { SeededRandom } from '../core/random';
import type { FactionId, SectorState, SystemId } from './types';

/** The satellite's visiting order: a seeded permutation of the systems, keyed by the sector's seed and the faction. */
export function passOrder(state: Pick<SectorState, 'seed' | 'systems'>, faction: FactionId): SystemId[] {
  const ids = state.systems.map(s => s.id);
  const rng = new SeededRandom(`${state.seed}-satellite-${faction}`);
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [ids[i], ids[j]] = [ids[j], ids[i]];
  }
  return ids;
}

/** The system a faction's satellite passes over on `day` (one a day; the orbit repeats every `systems.length` days). */
export function passOver(state: Pick<SectorState, 'seed' | 'systems'>, faction: FactionId, day: number): SystemId {
  const order = passOrder(state, faction);
  const n = Math.max(0, Math.floor(day));
  return order[n % order.length];
}

/** The next day on or after `day` that a faction's satellite passes `systemId`. */
export function nextPass(state: Pick<SectorState, 'seed' | 'systems'>, faction: FactionId, systemId: SystemId, day: number): number {
  const order = passOrder(state, faction);
  const idx = order.indexOf(systemId);
  if (idx < 0) return Infinity;
  const n = Math.max(0, Math.floor(day));
  const ahead = ((idx - (n % order.length)) + order.length) % order.length;
  return n + ahead;
}

/** Does an enemy of `faction` pass `systemId` on `day`? */
export function enemyPassOn(state: Pick<SectorState, 'seed' | 'systems' | 'factions'>, faction: FactionId, systemId: SystemId, day: number): FactionId | null {
  for (const f of state.factions) {
    if (f.id === faction) continue;
    if (passOver(state, f.id, day) === systemId) return f.id;
  }
  return null;
}

/**
 * The stand-in: should a sneak toward `target`, arriving on `arrivalDay`, wait a day? Yes when an enemy pass covers
 * the target that day and the day after is clear (a wait is one turn; the orbit moves on).
 */
export function waitForPass(state: Pick<SectorState, 'seed' | 'systems' | 'factions' | 'rules'>, faction: FactionId, target: SystemId, arrivalDay: number): boolean {
  if (!state.rules.satellites) return false;
  return enemyPassOn(state, faction, target, arrivalDay) !== null && enemyPassOn(state, faction, target, arrivalDay + 1) === null;
}

/** One line for the panel: when each side's satellite next passes this system. */
export function passesText(state: Pick<SectorState, 'seed' | 'systems' | 'factions' | 'day'>, viewer: FactionId, systemId: SystemId): string {
  const parts = state.factions.map(f => {
    const d = nextPass(state, f.id, systemId, state.day);
    const when = d <= Math.floor(state.day) ? 'today' : `day ${d}`;
    return `${f.id === viewer ? 'yours' : `${f.name}'s`} ${when}`;
  });
  return parts.join(' · ');
}
